import { FC, useState } from 'react'
import styles from './AdminCardMainInformation.module.css'
import iconEdit from '../../assets/icons/iconEdit.svg'
import CustomInput from '../UI/CustomInput/CustomInput'
import { Formik, FormikValues } from 'formik'
import * as yup from 'yup'

import { GetCurrentAdminResponseType } from '../../redux/actions/types'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import {
  blockAdmin,
  changeAdminMainInformation,
  getCurrentAdmin,
} from '../../redux/actions/adminsAction'
import s from '../../styles/common/Form.module.css'
import { useMediaQuery } from 'react-responsive'
import InputMask from 'react-input-mask'
import p from '../UI/CustomInput/CustomInput.module.css'
import CustomButton from '../UI/CustomButton/CustomButton'
import { ItemInfo } from '../ItemInfo/ItemInfo'

import { createRoleForRegister, createRoleForTable } from '../../utils/createRoleForRegister'
import usePasswordToggle from '../../hooks/usePasswordToggle'
import { useParams } from 'react-router-dom'
import CustomRadio from '../UI/CustomRadio/CustomRadio'
import BlockSwiper from '../BlockSwiper/BlockSwiper'
import { useToast } from '../../context/ToastContext'

export type AdminCardMainInformationPropsType = {
  adminData: GetCurrentAdminResponseType
}

const AdminCardMainInformation: FC<AdminCardMainInformationPropsType> = ({ adminData }) => {
  const [editMode, setEditMode] = useState(false)

  const { token, user } = useAppSelector((state) => state.auth)
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const [currentEmail, setCurrentEmail] = useState('')
  const [PasswordInputType, ToggleIcon] = usePasswordToggle()
  const { currentAdmin } = useAppSelector((state) => state.admins)
  const { showSuccess, showError } = useToast()
  const { adminId } = useParams<{ adminId: string }>()
  const onChangeSwipeCheckboxHandler = async (checked: boolean) => {
    const resultAction = await dispatch(
      blockAdmin({
        token,
        adminId: String(currentAdmin.id),
        isBlocked: !checked,
      }),
    )

    if (blockAdmin.rejected.match(resultAction)) {
      showError('Ошибка при блокировании пользователя')
    } else {
      showSuccess('Пользователь успешно заблокирован')
      await dispatch(getCurrentAdmin({ adminId, token }))
    }
  }

  const dispatch = useAppDispatch()

  const validationSchema = yup.object().shape({
    fullName: yup.string().required('Поле является обязательным'),
    phone: yup.string().required('Поле является обязательным'),
    role: yup
      .string()
      .required('Поле является обязательным')
      .oneOf(
        ['Суперадминистратор', 'Администратор', 'суперадминистратор', 'администратор'] as const,
        'Роль может быть только' + ' Суперадминистратор или Администратор',
      ),
    email: yup
      .string()
      .email('К сожалению, почта введена неверно, попробуйте еще раз')
      .required('Поле является обязательным'),
    password: yup
      .string()
      .required('Поле является обязательным')
      .min(8, 'Пароль должен' + ' содержать' + ' минимум 8' + ' символов'),
  })

  const handleSubmitHandler = async (values: FormikValues) => {
    const mobileWithoutSymbols = `+${values.phone.replace(/[\D]+/g, '')}`
    const role = createRoleForRegister(values.role)

    const resultAction = await dispatch(
      changeAdminMainInformation({
        token,
        adminId: String(adminData.id),
        fullName: values.fullName,
        phone: mobileWithoutSymbols,
        email: values.email,
        password: values.password,
        role,
      }),
    )

    if (changeAdminMainInformation.rejected.match(resultAction)) {
      showError('Ошибка при изменении данных')
    } else {
      showSuccess('Данные успешно обновлены')
    }
    setCurrentEmail(values.email)
  }

  const handleEditMode = () => {
    setEditMode(true)
    setCurrentEmail(adminData.email)
  }

  return (
    <div className={styles.mainInformation}>
      {editMode ? (
        <div className={styles.editMode}>
          <Formik
            initialValues={{
              fullName: adminData.fullName,
              phone: adminData.phone,
              role: createRoleForTable(adminData.role),
              email: adminData.email,
              password: adminData.password,
            }}
            validateOnBlur
            onSubmit={(values) => handleSubmitHandler(values)}
            validationSchema={validationSchema}
          >
            {(props) => {
              const { values, touched, errors, handleChange, handleBlur, handleSubmit } = props

              return (
                <div className={styles.staticInformation}>
                  <div className={styles.header}>
                    <h2 className={styles.title}>Основная информация</h2>
                    {!isMobile && (
                      <div className={styles.btnContainer}>
                        <CustomButton
                          className={styles.btn}
                          title={'Отменить'}
                          callbackHandler={() => setEditMode(false)}
                        />
                        <CustomButton
                          className={styles.submitBtn}
                          title={'Cохранить'}
                          callbackHandler={handleSubmit}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.container}>
                    <div className={styles.inputContainer}>
                      <div className={styles.subtitle}>Роль</div>
                      <div className={styles.inputContainerWithError}>
                        <CustomRadio
                          options={['Суперадминистратор', 'Администратор']}
                          name='role'
                          onChange={handleChange}
                          value={values.role}
                        />
                        {touched.role && errors.role && (
                          <p style={{ textAlign: 'start' }} className={s.errorValidation}>
                            {errors.role}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <div className={styles.subtitle}>ФИО</div>
                      <div className={styles.inputContainerWithError}>
                        <CustomInput
                          type='text'
                          placeholder=''
                          name='fullName'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.fullName}
                          className={
                            errors.fullName && touched.fullName
                              ? `${styles.inputError} ${styles.input}`
                              : styles.input
                          }
                        />
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <div className={styles.subtitle}>Номер телефона</div>
                      <div className={styles.inputContainerWithError}>
                        <InputMask
                          mask='+7 (999) 999 9999'
                          type='phone'
                          placeholder=''
                          name='phone'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phone}
                          className={
                            errors.phone && touched.phone
                              ? `${s.inputError} ${p.input} ${styles.input}`
                              : `${p.input} ${styles.input}`
                          }
                        ></InputMask>
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <div className={styles.subtitle}>Электронная почта/Логин</div>
                      <div className={styles.inputContainerWithError}>
                        <CustomInput
                          type='text'
                          placeholder=''
                          name='email'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          className={
                            errors.email && touched.email
                              ? `${styles.inputError} ${styles.input}`
                              : styles.input
                          }
                        />
                      </div>
                    </div>
                    <div className={styles.inputContainer}>
                      <div className={styles.subtitle}>Пароль</div>
                      <div className={styles.inputContainerWithError}>
                        <div className={styles.passInput}>
                          <CustomInput
                            type={PasswordInputType}
                            placeholder=''
                            name='password'
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            className={
                              errors.password && touched.password
                                ? `${styles.inputError} ${styles.input}`
                                : styles.input
                            }
                          />
                          <div className={styles.eyeIcon}>{ToggleIcon}</div>
                        </div>
                        {touched.password && errors.password && (
                          <p style={{ textAlign: 'start' }} className={s.errorValidation}>
                            {errors.password}
                          </p>
                        )}
                      </div>
                    </div>
                    {currentAdmin.id != user.id && (
                      <div
                        style={{ flexDirection: 'row', justifyContent: 'space-between' }}
                        className={styles.inputContainer}
                      >
                        <div className={styles.subtitle}>Заблокировать</div>

                        <BlockSwiper
                          isBlocked={currentAdmin.isBlocked}
                          onChangeHandler={onChangeSwipeCheckboxHandler}
                        />
                      </div>
                    )}
                  </div>
                  {isMobile && (
                    <div className={styles.btnContainer}>
                      <CustomButton
                        className={styles.cancelBtn}
                        title={'Отменить'}
                        callbackHandler={() => setEditMode(false)}
                      />
                      <CustomButton
                        className={styles.submitBtn}
                        title={'Cохранить'}
                        callbackHandler={handleSubmit}
                      />
                    </div>
                  )}
                </div>
              )
            }}
          </Formik>
        </div>
      ) : (
        <div className={styles.staticInformation}>
          <div className={styles.header}>
            <h2 className={styles.title}>Основная информация</h2>
            <CustomButton
              iconSrc={iconEdit}
              className={styles.btn}
              title={''}
              onClick={handleEditMode}
            />
          </div>
          <div className={styles.container}>
            <ItemInfo title='ФИО' value={adminData.fullName} />
            <ItemInfo title='Номер телефона' value={adminData.phone} />
            <ItemInfo title='Роль' value={createRoleForTable(adminData.role) ?? ''} />
            <ItemInfo title='Электронная почта/Логин' value={adminData.email} />
            <ItemInfo title='Пароль' value={'******'} />
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCardMainInformation
